import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import DocHeader from "@/components/DocHeader";
import { SUBJECT_META } from "@/lib/scale";
import { ArrowLeft, Printer, Loader2, Sprout, Sparkles, Save, Plus, Trash2, FileEdit } from "lucide-react";

const SUBJECTS = [
  { key: "literacy", name: "Literacy" },
  { key: "numeracy", name: "Numeracy" },
  { key: "social_emotional", name: "Social & Emotional" },
];
const TYPES = [
  { key: "short_term", name: "Short-Term Plan" },
  { key: "cuntas", name: "Cuntas Míosúil" },
];
const HEADINGS = [
  ["learning_outcomes", "Learning Outcomes"],
  ["focus_of_learning", "Focus of Learning"],
  ["learning_experiences", "Learning Experiences"],
  ["teaching_strategies", "Teaching Strategies"],
  ["assessment", "Assessment"],
  ["reflection", "Reflection / Assessment Notes"],
];
const EMPTY = Object.fromEntries(HEADINGS.map(([k]) => [k, ""]));

export default function Planning() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [cls, setCls] = useState(null);
  const [plans, setPlans] = useState([]);
  const [subject, setSubject] = useState("literacy");
  const [planType, setPlanType] = useState("short_term");
  const [period, setPeriod] = useState("");
  const [content, setContent] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/classes").then((r) => setCls(r.data.find((c) => c.id === classId)));
    api.get(`/plans?class_id=${classId}`).then((r) => setPlans(r.data)).catch(() => {});
  }, [classId]);
  useEffect(() => { load(); }, [load]);

  const reset = () => { setContent(EMPTY); setPeriod(""); setEditingId(null); };

  const edit = (p) => {
    setEditingId(p.id); setSubject(p.subject); setPlanType(p.plan_type);
    setPeriod(p.period || ""); setContent({ ...EMPTY, ...p.content });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/plans/generate", { class_id: classId, plan_type: planType, subject, period });
      setContent({ ...EMPTY, ...data.content });
      toast.success("Draft ready — review and edit");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { class_id: classId, plan_type: planType, subject, period, content };
      if (editingId) await api.put(`/plans/${editingId}`, body);
      else await api.post("/plans", body);
      toast.success("Plan saved");
      reset();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => { await api.delete(`/plans/${id}`); if (id === editingId) reset(); load(); };

  return (
    <div className="min-h-screen bg-[#F9F9F7] py-8 px-4">
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-3 mb-6 no-print flex-wrap">
        <button data-testid="planning-back-btn" onClick={() => navigate(`/class/${classId}`)} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition">
          <ArrowLeft size={16} /> Back to class
        </button>
        <button data-testid="planning-print-btn" onClick={() => window.print()} className="bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2">
          <Printer size={17} /> Print
        </button>
      </div>

      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-2.5 mb-1">
          <span className="h-9 w-9 rounded-xl bg-[#81B29A] flex items-center justify-center text-white"><Sprout size={18} /></span>
          <span className="font-['Outfit'] font-bold text-lg text-slate-800">BloomTrack · Planning</span>
        </div>
        <h1 className="font-['Outfit'] text-3xl font-bold text-slate-800">{cls?.name || "Class"} <span className="text-slate-400 text-xl font-normal">· {cls?.level}</span></h1>

        {/* Editor */}
        <div className="print-area bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.06)] p-6 sm:p-8 mt-5">
          <DocHeader printOnly subtitle="Class Planning" />
          <div className="flex flex-wrap items-center gap-3 no-print">
            <div className="flex gap-1.5 bg-[#F9F9F7] p-1 rounded-full">
              {SUBJECTS.map((s) => (
                <button key={s.key} data-testid={`plan-subject-${s.key}`} onClick={() => setSubject(s.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold font-['Outfit'] transition ${subject === s.key ? "text-white" : "text-slate-500"}`}
                  style={subject === s.key ? { backgroundColor: SUBJECT_META[s.key].tint } : {}}>{s.name}</button>
              ))}
            </div>
            <div className="flex gap-1.5 bg-[#F9F9F7] p-1 rounded-full">
              {TYPES.map((t) => (
                <button key={t.key} data-testid={`plan-type-${t.key}`} onClick={() => setPlanType(t.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold font-['Outfit'] transition ${planType === t.key ? "bg-[#4A5D6B] text-white" : "text-slate-500"}`}>{t.name}</button>
              ))}
            </div>
            <input data-testid="plan-period-input" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="Period (e.g. Sept Wk 1-2 / October)"
              className="flex-1 min-w-[180px] bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#81B29A] focus:bg-white" />
          </div>

          {/* Print header */}
          <div className="hidden print:block mb-4">
            <h2 className="font-bold text-lg">{TYPES.find((t) => t.key === planType)?.name} — {SUBJECTS.find((s) => s.key === subject)?.name}{period ? ` · ${period}` : ""}</h2>
          </div>

          <div className="mt-5 space-y-4">
            {HEADINGS.map(([k, label]) => (
              <div key={k}>
                <h3 className="font-['Outfit'] text-base font-bold px-3 py-1.5 rounded-lg" style={{ backgroundColor: SUBJECT_META[subject].tint + "22", color: "#2a3b47" }}>{label}</h3>
                <textarea data-testid={`plan-${k}`} rows={3} value={content[k]} onChange={(e) => setContent({ ...content, [k]: e.target.value })}
                  className="print:hidden mt-2 w-full bg-[#F9F9F7] border border-slate-100 rounded-lg px-3.5 py-2.5 text-sm text-slate-700 outline-none focus:border-[#81B29A] focus:bg-white resize-none transition whitespace-pre-wrap" />
                <div className="hidden print:block mt-2 text-sm text-slate-700 whitespace-pre-wrap px-3.5 py-2.5 border border-slate-200 rounded-lg min-h-[1.5rem]">{content[k] || "—"}</div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 mt-5 no-print flex-wrap">
            <button data-testid="plan-generate-btn" onClick={generate} disabled={generating}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold font-['Outfit'] text-white transition disabled:opacity-60"
              style={{ background: "linear-gradient(135deg,#81B29A,#5d8a73)" }}>
              {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {generating ? "Generating…" : "Generate with AI"}
            </button>
            <button data-testid="plan-save-btn" onClick={save} disabled={saving}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold font-['Outfit'] bg-[#4A5D6B] text-white hover:bg-[#3A4A56] transition disabled:opacity-60">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {editingId ? "Update plan" : "Save plan"}
            </button>
            {editingId && <button data-testid="plan-new-btn" onClick={reset} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold font-['Outfit'] bg-white border border-slate-200 text-slate-600 hover:border-slate-300"><Plus size={15} /> New</button>}
          </div>
        </div>

        {/* Saved plans */}
        <h2 className="font-['Outfit'] text-xl font-bold text-slate-800 mt-8 mb-3 no-print">Saved plans</h2>
        {plans.length === 0 ? (
          <p className="text-sm text-slate-400 no-print">No saved plans yet.</p>
        ) : (
          <div className="space-y-2.5 no-print">
            {plans.map((p) => (
              <div key={p.id} data-testid={`plan-row-${p.id}`} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-3">
                <span className="text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ backgroundColor: SUBJECT_META[p.subject].tint + "22", color: SUBJECT_META[p.subject].tint }}>{SUBJECTS.find((s) => s.key === p.subject)?.name}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-['Outfit'] font-semibold text-slate-800 text-sm">{TYPES.find((t) => t.key === p.plan_type)?.name}{p.period ? ` · ${p.period}` : ""}</p>
                  <p className="text-xs text-slate-400">Updated {new Date(p.updated_at).toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "numeric" })}</p>
                </div>
                <button data-testid={`edit-plan-${p.id}`} onClick={() => edit(p)} className="text-slate-400 hover:text-[#4A5D6B] p-1.5"><FileEdit size={16} /></button>
                <button data-testid={`delete-plan-${p.id}`} onClick={() => remove(p.id)} className="text-slate-300 hover:text-red-500 p-1.5"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
